import { useCallback, useEffect, useRef, forwardRef, type TextareaHTMLAttributes } from "react";

interface AutoResizeTextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  /** Minimum height in pixels */
  minHeight?: number;
  /** Maximum height in pixels */
  maxHeight?: number;
}

/**
 * A textarea that automatically resizes based on its content.
 * This is a cross-browser alternative to CSS `field-sizing: content`
 * which is not supported in Safari or Firefox.
 */
export const AutoResizeTextarea = forwardRef<HTMLTextAreaElement, AutoResizeTextareaProps>(
  ({ minHeight = 0, maxHeight = Infinity, onChange, style, ...props }, forwardedRef) => {
    const internalRef = useRef<HTMLTextAreaElement | null>(null);

    // Merge refs: attach to both internal ref and forwarded ref
    const mergeRefs = useCallback(
      (element: HTMLTextAreaElement | null) => {
        internalRef.current = element;
        if (typeof forwardedRef === "function") {
          forwardedRef(element);
        } else if (forwardedRef) {
          // Type assertion needed because RefObject.current is readonly in the type definition,
          // but we need to assign to it when merging refs
          const mutableRef = forwardedRef as { current: HTMLTextAreaElement | null };
          mutableRef.current = element;
        }
      },
      [forwardedRef]
    );

    const resize = useCallback(() => {
      const textarea = internalRef.current;
      if (!textarea) return;

      // Reset height to auto to get the correct scrollHeight
      textarea.style.height = "auto";

      // Calculate new height within bounds
      const newHeight = Math.min(Math.max(textarea.scrollHeight, minHeight), maxHeight);
      textarea.style.height = `${newHeight}px`;

      // Add overflow if content exceeds maxHeight
      textarea.style.overflowY = textarea.scrollHeight > maxHeight ? "auto" : "hidden";
    }, [minHeight, maxHeight]);

    // Resize on mount and when value changes
    useEffect(() => {
      resize();
    }, [resize, props.value]);

    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        onChange?.(e);
        resize();
      },
      [onChange, resize]
    );

    return (
      <textarea
        ref={mergeRefs}
        onChange={handleChange}
        style={{
          ...style,
          minHeight: minHeight ? `${minHeight}px` : undefined,
          maxHeight: maxHeight !== Infinity ? `${maxHeight}px` : undefined,
          overflow: "hidden",
        }}
        {...props}
      />
    );
  }
);

AutoResizeTextarea.displayName = "AutoResizeTextarea";
